// features/loyalty/loyalty.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchLoyaltyDashboardApi, fetchLoyaltyStatusApi, fetchLoyaltyPointsHistoryApi, LoyaltyDashboardData, LoyaltyStatusData, LoyaltyPointsHistoryResponse } from './loyalty.service';
import { logOut } from '../../../redux/auth/authSlice';

export const fetchLoyaltyDashboard = createAsyncThunk(
    'loyalty/fetchLoyaltyDashboard',
    async (_, { rejectWithValue }) => {
        try {
            const data = await fetchLoyaltyDashboardApi();
            return data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const fetchLoyaltyStatus = createAsyncThunk(
    'loyalty/fetchLoyaltyStatus',
    async (_, { rejectWithValue }) => {
        try {
            const data = await fetchLoyaltyStatusApi();
            return data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const fetchLoyaltyPointsHistory = createAsyncThunk(
    'loyalty/fetchLoyaltyPointsHistory',
    async ({ pageNumber = 1, pageSize = 50 }: { pageNumber?: number; pageSize?: number } = {}, { rejectWithValue }) => {
        try {
            const data = await fetchLoyaltyPointsHistoryApi(pageNumber, pageSize);
            return data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

type LoyaltyState = {
    loyaltyData: Awaited<ReturnType<typeof fetchLoyaltyDashboardApi>> | null;
    loyaltyStatusData: Awaited<ReturnType<typeof fetchLoyaltyStatusApi>> | null;
    loyaltyPointsHistoryData: Awaited<ReturnType<typeof fetchLoyaltyPointsHistoryApi>> | null;
    loading: boolean;
    statusLoading: boolean;
    pointsHistoryLoading: boolean;
    error: string | null;
    statusError: string | null;
    pointsHistoryError: string | null;
};

const initialState: LoyaltyState = {
    loyaltyData: null,
    loyaltyStatusData: null,
    loyaltyPointsHistoryData: null,
    loading: false,
    statusLoading: false,
    pointsHistoryLoading: false,
    error: null,
    statusError: null,
    pointsHistoryError: null,
};

const loyaltySlice = createSlice({
    name: 'loyalty',
    initialState,
    reducers: {
        clearLoyaltyData: (state) => {
            state.loyaltyData = null;
            state.loyaltyStatusData = null;
            state.loyaltyPointsHistoryData = null;
            state.error = null;
            state.statusError = null;
            state.pointsHistoryError = null;
        },
    },
    extraReducers: builder => {
        builder
            // Loyalty Dashboard Cases
            .addCase(fetchLoyaltyDashboard.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLoyaltyDashboard.fulfilled, (state, action) => {
                state.loading = false;
                state.loyaltyData = action.payload;
            })
            .addCase(fetchLoyaltyDashboard.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch loyalty dashboard data';
            })
            // Loyalty Status Cases
            .addCase(fetchLoyaltyStatus.pending, state => {
                state.statusLoading = true;
                state.statusError = null;
            })
            .addCase(fetchLoyaltyStatus.fulfilled, (state, action) => {
                state.statusLoading = false;
                state.loyaltyStatusData = action.payload;
            })
            .addCase(fetchLoyaltyStatus.rejected, (state, action) => {
                state.statusLoading = false;
                state.statusError = action.error.message || 'Failed to fetch loyalty status data';
            })
            // Loyalty Points History Cases
            .addCase(fetchLoyaltyPointsHistory.pending, state => {
                state.pointsHistoryLoading = true;
                state.pointsHistoryError = null;
            })
            .addCase(fetchLoyaltyPointsHistory.fulfilled, (state, action) => {
                state.pointsHistoryLoading = false;
                state.loyaltyPointsHistoryData = action.payload;
            })
            .addCase(fetchLoyaltyPointsHistory.rejected, (state, action) => {
                state.pointsHistoryLoading = false;
                state.pointsHistoryError = action.error.message || 'Failed to fetch loyalty points history data';
            })
            // Reset state on logout
            .addCase(logOut, () => initialState);
    },
});

export const { clearLoyaltyData } = loyaltySlice.actions;
export default loyaltySlice.reducer;
